import { Dayjs } from "dayjs"
import { Duration } from "../../../common/durationModel"

export type EventFormValues = {
  startDate: Dayjs | null
  startTime: Dayjs | null
  estimatedDuration: Duration | null
  crewMemberIds: string
  crewLeadId: number | null
  workareaIds: string
  repeatUnit: string
  repeatCount: number
  repeatPlace: string[]
  repeatQuarter: number[]
  repeatMonth: number[]
  repeatEndsOn: Dayjs | null
  notes: string
}

const arraysEqual = (a: any[], b: any[]): boolean => {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((val, idx) => val === sortedB[idx])
}

const timesMatch = (first: Dayjs | null, second: Dayjs | null): boolean => {
  if (first && second) {
    return first.hour() === second.hour() && first.minute() === second.minute()
  }
  return first === second
}

const durationsMatch = (first: Duration | null, second: Duration | null): boolean => {
  if (first && second) {
    return first.seconds === second.seconds
  }
  return first === second
}

type FieldChanges = Record<keyof EventFormValues, boolean>

type ChangedField = keyof FieldChanges

const compareFormValues = (original: EventFormValues, current: EventFormValues): FieldChanges => ({
  startDate: !(current.startDate?.isSame(original.startDate, 'day') ?? false),
  startTime: !timesMatch(current.startTime, original.startTime),
  estimatedDuration: !durationsMatch(current.estimatedDuration, original.estimatedDuration),
  crewMemberIds: current.crewMemberIds !== original.crewMemberIds,
  crewLeadId: current.crewLeadId !== original.crewLeadId,
  workareaIds: current.workareaIds !== original.workareaIds,
  repeatUnit: current.repeatUnit !== original.repeatUnit,
  repeatCount: current.repeatCount !== original.repeatCount,
  repeatPlace: !arraysEqual(current.repeatPlace, original.repeatPlace),
  repeatQuarter: !arraysEqual(current.repeatQuarter, original.repeatQuarter),
  repeatMonth: !arraysEqual(current.repeatMonth, original.repeatMonth),
  repeatEndsOn: !(
    (current.repeatEndsOn === null && original.repeatEndsOn === null) ||
    (current.repeatEndsOn?.isSame(original.repeatEndsOn, 'day') ?? false)
  ),
  notes: current.notes !== original.notes,
})

const getChangedFields = (original: EventFormValues, current: EventFormValues): ChangedField[] => {
  const changes = compareFormValues(original, current)
  return (Object.keys(changes) as ChangedField[]).filter(key => changes[key])
}

export const shouldAutoApplyFutureUpdate = (
  original: EventFormValues,
  current: EventFormValues,
  hasRepeatingId: boolean
): boolean => {
  if (!hasRepeatingId) {
    return false
  }
  const changed = getChangedFields(original, current).filter(field => field !== 'notes')
  return changed.length === 1 && changed[0] === 'repeatEndsOn'
}

export const shouldAllowRouteFutureUpdate = (
  original: EventFormValues,
  current: EventFormValues
): boolean => {
  const changed = getChangedFields(original, current)
  return changed.length === 1 && changed[0] === 'notes'
}
